import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/AppLayout";
import { StatusBadge } from "../components/StatusBadge";
import {
  Moon,
  CalendarCheck,
  AlertTriangle,
  Wrench,
  FileClock,
  ArrowRight,
  TrendingUp,
  Trophy,
} from "lucide-react";
import {
  kpisHome,
  CONVERSACIONES,
  VISITAS,
  COBROS,
  RECLAMOS,
  CONTRATOS,
  AGENTES,
  INTERESADOS,
} from "../data/mockData";
import { pesos, fechaCorta } from "../lib/format";
import { useBrand } from "../context/BrandContext";
import { useTasks } from "../context/TasksContext";

function Kpi({ icon: Icon, label, valor, detalle, tone = "slate", testId, onClick }) {
  const tones = {
    slate: "text-slate-700 bg-slate-100",
    purple: "text-purple-700 bg-purple-50",
    red: "text-red-700 bg-red-50",
    yellow: "text-yellow-700 bg-yellow-50",
    blue: "text-blue-700 bg-blue-50",
  };
  return (
    <Card
      onClick={onClick}
      data-testid={testId}
      className="p-5 rounded-lg border-slate-200 shadow-sm cursor-pointer transition-colors hover:border-slate-300"
    >
      <div className="flex items-start justify-between">
        <div className={`flex h-10 w-10 items-center justify-center rounded-md ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <ArrowRight className="h-4 w-4 text-slate-300" />
      </div>
      <p className="mt-4 text-2xl font-bold text-slate-900">{valor}</p>
      <p className="text-sm font-medium text-slate-600">{label}</p>
      {detalle && <p className="mt-1 text-xs text-slate-400">{detalle}</p>}
    </Card>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { brand } = useBrand();
  const { tareas } = useTasks();
  const k = kpisHome();

  const ranking = [...AGENTES]
    .map((ag) => ({
      ag,
      leads: INTERESADOS.filter((l) => l.agenteId === ag.id).length,
      cerradas: INTERESADOS.filter((l) => l.agenteId === ag.id && l.estado === "Cerrado").length,
      visitas: VISITAS.filter((v) => v.agenteId === ag.id).length,
      tareas: tareas.filter((t) => t.agenteId === ag.id && !t.hecha).length,
    }))
    .sort((a, b) => b.cerradas - a.cerradas || b.leads - a.leads);

  const fueraHorario = CONVERSACIONES.filter((c) => c.fueraDeHorario).slice(0, 4);
  const proximasVisitas = [...VISITAS]
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
    .slice(0, 5);
  const atrasados = COBROS.filter((c) => c.estado === "Atrasado").slice(0, 5);
  const reclamos = RECLAMOS.filter((r) => r.estado !== "Cerrado").slice(0, 4);
  const ajustan = CONTRATOS.filter((c) => {
    const d = (new Date(c.proximaActualizacion) - new Date()) / 86400000;
    return d >= 0 && d <= 30;
  }).slice(0, 4);

  return (
    <div>
      <PageHeader
        titulo={`Hola, equipo de ${brand.nombre.split(" ")[0]}`}
        descripcion="Este es el panel del dueño: todo lo que pasó mientras la oficina estaba cerrada y lo que hay que atender esta semana."
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi
          icon={Moon}
          tone="purple"
          label="Consultas fuera de horario"
          valor={k.fueraDeHorario}
          detalle="Respondidas por el asistente esta semana"
          testId="kpi-card-out-of-hours"
          onClick={() => navigate("/asistente")}
        />
        <Kpi
          icon={CalendarCheck}
          tone="blue"
          label="Visitas esta semana"
          valor={k.visitasSemana}
          detalle="Coordinadas y por coordinar"
          testId="kpi-card-visits"
          onClick={() => navigate("/visitas")}
        />
        <Kpi
          icon={AlertTriangle}
          tone="red"
          label="Cobros por cobrar"
          valor={k.cobrosPendientes + k.cobrosAtrasados}
          detalle={`${pesos(k.montoPendiente + k.montoAtrasado)} · ${k.cobrosAtrasados} atrasados`}
          testId="kpi-card-overdue-payments"
          onClick={() => navigate("/alquileres")}
        />
        <Kpi
          icon={Wrench}
          tone="yellow"
          label="Reclamos abiertos"
          valor={k.reclamosAbiertos}
          detalle="Mantenimiento en curso"
          testId="kpi-card-claims"
          onClick={() => navigate("/alquileres")}
        />
        <Kpi
          icon={FileClock}
          tone="slate"
          label="Contratos a atender"
          valor={k.porVencer + k.porAjustar}
          detalle={`${k.porVencer} por vencer · ${k.porAjustar} ajustan`}
          testId="kpi-card-expiring-contracts"
          onClick={() => navigate("/alquileres")}
        />
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-900">Ranking de vendedores · este mes</h3>
          <Button variant="ghost" size="sm" onClick={() => navigate("/tareas")}>Ver tareas</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="agent-panel">
          {ranking.map((r, idx) => {
            const iniciales = r.ag.nombre.split(" ").map((p) => p[0]).join("").slice(0, 2);
            const pct = Math.min(100, Math.round((r.visitas / r.ag.metaVisitas) * 100));
            return (
              <Card key={r.ag.id} data-testid={`agent-card-${r.ag.id}`} className="p-4 rounded-lg border-slate-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md text-sm font-bold text-white" style={{ backgroundColor: r.ag.color }}>{iniciales}</div>
                    <span className={`absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-md text-[10px] font-bold ${idx === 0 ? "bg-amber-400 text-amber-900" : "bg-slate-200 text-slate-600"}`}>{idx + 1}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1">
                      {r.ag.nombre} {idx === 0 && <Trophy className="h-3.5 w-3.5 text-amber-500" />}
                    </p>
                    <p className="text-xs text-slate-400">{r.cerradas} operaciones cerradas</p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <button onClick={() => navigate("/propiedades")} className="rounded-md bg-slate-50 px-2 py-2 hover:bg-slate-100 transition-colors" data-testid={`agent-closed-${r.ag.id}`}>
                    <p className="text-base font-bold text-slate-900">{r.cerradas}</p>
                    <p className="text-[11px] text-slate-500">Cerradas</p>
                  </button>
                  <button onClick={() => navigate("/propiedades")} className="rounded-md bg-slate-50 px-2 py-2 hover:bg-slate-100 transition-colors" data-testid={`agent-leads-${r.ag.id}`}>
                    <p className="text-base font-bold text-slate-900">{r.leads}</p>
                    <p className="text-[11px] text-slate-500">Leads</p>
                  </button>
                  <button onClick={() => navigate("/tareas")} className="rounded-md bg-slate-50 px-2 py-2 hover:bg-slate-100 transition-colors" data-testid={`agent-tasks-${r.ag.id}`}>
                    <p className="text-base font-bold text-slate-900">{r.tareas}</p>
                    <p className="text-[11px] text-slate-500">Tareas</p>
                  </button>
                </div>
                <div className="mt-3">
                  <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                    <span>Meta de visitas</span>
                    <span className="font-medium text-slate-700">{r.visitas}/{r.ag.metaVisitas}</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden" data-testid={`agent-goal-${r.ag.id}`}>
                    <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-lg border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Moon className="h-4 w-4 text-purple-600" />
              <h3 className="text-base font-semibold text-slate-900">
                Consultas respondidas fuera de horario
              </h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/asistente")}>
              Ver bandeja
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {fueraHorario.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-5 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{c.contacto.nombre}</p>
                  <p className="truncate text-xs text-slate-500">
                    {c.propiedadTitulo} · {c.canal} · {c.horaActividad} hs
                  </p>
                </div>
                <StatusBadge estado="Fuera de horario" className="bg-purple-50 text-purple-800 border-purple-200" />
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-lg border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <CalendarCheck className="h-4 w-4 text-blue-600" />
              <h3 className="text-base font-semibold text-slate-900">Próximas visitas</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/visitas")}>
              Coordinar
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {proximasVisitas.map((v) => (
              <div key={v.id} className="flex items-center justify-between px-5 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{v.direccion}</p>
                  <p className="truncate text-xs text-slate-500">
                    {v.interesado.nombre} · {fechaCorta(v.fecha)} {v.hora} hs · {v.tipo}
                  </p>
                </div>
                <StatusBadge estado={v.estado} />
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-lg border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <h3 className="text-base font-semibold text-slate-900">Cobros atrasados</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/alquileres")}>
              Ver cobros
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {atrasados.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-5 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{c.inquilino}</p>
                  <p className="truncate text-xs text-slate-500">
                    {c.direccion} · vencía {fechaCorta(c.vencimiento)}
                  </p>
                </div>
                <span className="text-sm font-semibold text-slate-900">{pesos(c.monto)}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-lg border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-slate-600" />
              <h3 className="text-base font-semibold text-slate-900">Contratos que ajustan este mes</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/alquileres")}>
              Ver contratos
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {ajustan.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-5 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{c.direccion}</p>
                  <p className="truncate text-xs text-slate-500">
                    Índice {c.indice} · {fechaCorta(c.proximaActualizacion)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400 line-through">{pesos(c.montoMensual)}</p>
                  <p className="text-sm font-semibold text-emerald-700">{pesos(c.proximoMontoEstimado)}</p>
                </div>
              </div>
            ))}
            {reclamos.length > 0 && (
              <div className="px-5 py-3 bg-slate-50">
                <p className="text-xs text-slate-500">
                  Además hay <span className="font-semibold text-slate-700">{k.reclamosAbiertos} reclamos</span> de mantenimiento abiertos.
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
